import {
  blank,
  code,
  fromMarkdown,
  lines,
  pad,
  separator,
  statusbar,
  tree,
  vstack,
  type LayoutFn,
} from "../src";
import { BC, C, MD_STYLE, SPINNER_FRAMES, setTermLayout, type DemoTermElement } from "./shared";

const UCW = 48;

const DEV_TREE_DATA = [
  {
    label: "src",
    children: [
      { label: "index.ts" },
      { label: "termui.ts" },
      { label: "term-element.ts" },
    ],
  },
  { label: "package.json" },
];

const DEV_CODE_VALUE = "const app = serve({\n  port: 3000,\n  fetch: handler,\n});";

const AI_MD = `# Build vs. Buy

There are reliable signals for this.

- **Build** when the last 10% is the whole point
- **Buy** when the edge cases outnumber your patience
- **Neither** when the real answer is "you don't need this yet"

> You can build it in a weekend. You'll also maintain it every weekend after that.
`;

function aiIntro(): LayoutFn {
  return vstack(
    lines([[{ text: pad(" ai chat", UCW), color: C.yellow, bold: true }]]),
    separator(C.border),
    blank(),
    lines([[{ text: " > build vs buy?", color: C.cyan }]]),
    blank(),
    separator(C.border),
    blank(),
  );
}

function devLayout(): LayoutFn {
  return vstack(
    lines([[{ text: pad(" dev tools", UCW), color: C.yellow, bold: true }]]),
    separator(C.border),
    tree(DEV_TREE_DATA, { branchColor: BC, leafColor: C.green }),
    blank(),
    code(DEV_CODE_VALUE, { language: "typescript", borderColor: BC, theme: C }),
  );
}

function randomInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

export function mountUseCases(aiTerm: DemoTermElement, devTerm: DemoTermElement): () => void {
  let cursor = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const renderAI = () => {
    const streamed = AI_MD.slice(0, cursor);
    const done = cursor >= AI_MD.length;
    const spinner = SPINNER_FRAMES[Math.floor(cursor / 6) % SPINNER_FRAMES.length] ?? SPINNER_FRAMES[0]!;

    const replayLine = done
      ? lines([[
          {
            text: " [ tell me that again ]",
            color: C.brightBlack,
            interactionId: "use-cases:ai:replay",
            onClick: () => {
              cursor = 0;
              schedule();
              renderAI();
            },
          },
        ]])
      : blank(0);

    const spinnerLine = done
      ? blank(0)
      : lines([[{ text: ` ${spinner}`, color: C.brightBlack }]]);

    setTermLayout(
      aiTerm,
      vstack(
        aiIntro(),
        fromMarkdown(streamed, { theme: C, overrides: MD_STYLE }),
        spinnerLine,
        blank(),
        replayLine,
        statusbar(
          [{ text: " model: claude-opus-4" }],
          [{ text: done ? "done " : "streaming... " }],
          { bg: "#1a1a2e", color: C.white },
        ),
      ),
    );
  };

  const schedule = () => {
    if (timer) {
      clearTimeout(timer);
      timer = undefined;
    }
    if (cursor >= AI_MD.length) return;

    timer = setTimeout(() => {
      cursor = Math.min(cursor + randomInt(2, 8), AI_MD.length);
      renderAI();
      schedule();
    }, randomInt(20, 60));
  };

  setTermLayout(devTerm, devLayout());
  renderAI();
  schedule();

  return () => {
    if (timer) clearTimeout(timer);
  };
}
